import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import {
  applyAnalyticsPendingLoading,
  markAnalyticsFetchKey,
} from '../../utils/analyticsQuery';
import dayjs from 'dayjs';

/* ----------------------------------------------------------
   ✅ Thunk: Fetch Analytics Ticket Stats (Core API call)
----------------------------------------------------------- */
export const getAnalyticsAgentsStats = createAsyncThunk(
  'analyticsAgentsStats/getAnalyticsAgentsStats',
  async ({ range = '7d', startDate = null, endDate = null, group_id }, { getState, rejectWithValue }) => {
    try {
      const { loginToken, analyticsAgentsStats } = getState();
      const authToken = loginToken?.response?.token;
      const business_id = loginToken?.response?.user?.business_id;


      const timezone = loginToken?.response?.user?.timezone || 'Asia/Karachi';

      const currentRange = analyticsAgentsStats?.currentRange || range;

      // Normalize range aliases
      let effectiveRange = currentRange;
      if (currentRange === '365d') effectiveRange = '1y';
      else if (currentRange === '1d') effectiveRange = 'today';

      // Build query params
      let queryParams = '';

      // Only send custom dates if range is actually 'custom'
      if (currentRange === 'custom' && startDate && endDate) {
        queryParams = `from=${startDate}&to=${endDate}&range=custom&timezone=${timezone}`;
      } else {
        // For preset ranges (today, yesterday, 7d, 30d, 1y)
        queryParams = `range=${effectiveRange}&timezone=${timezone}`;
      }
      if (group_id) {
        queryParams += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const response = await axios.get(
        `${ROOT_URL}/analytics/agent/stats/${business_id}?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* ----------------------------------------------------------
   ✅ Thunk: Initialize Analytics to default 7-day range
----------------------------------------------------------- */
export const initAnalyticsAgentsStats = createAsyncThunk(
  'analyticsAgentsStats/initAnalyticsAgentsStats',
  async ({ group_id } = {}, { dispatch }) => {
    const startDate = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');

    dispatch(setDateRange({ startDate, endDate }));

    await dispatch(getAnalyticsAgentsStats({ range: '7d', group_id }));
  }
);

/* ----------------------------------------------------------
   ✅ Initial State
----------------------------------------------------------- */
const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  currentRange: '7d',
  dateRange: {
    startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  },
  currentGroup: 'All Groups',
};

/* ----------------------------------------------------------
   ✅ Slice
----------------------------------------------------------- */
const analyticsAgentsStatsSlice = createSlice({
  name: 'analyticsAgentsStats',
  initialState,
  reducers: {
    resetAnalyticsAgentsStatsState: (state) => {
      state.loading = false;
      state.error = null;
      state.data = null;
      state.lastFetchKey = null;
      state.currentRange = '7d';
      state.dateRange = {
        startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      };
      state.currentGroup = 'All Groups';
    },
    setCurrentRange: (state, action) => {
      state.currentRange = action.payload;
    },
    setDateRange: (state, action) => {
      const { startDate, endDate } = action.payload;
      state.dateRange = { startDate, endDate };

      // Automatically determine range type
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const today = dayjs().startOf('day');
      const yesterday = today.subtract(1, 'day');

      const isToday = start.isSame(today, 'day') && end.isSame(today, 'day');
      const isYesterday = start.isSame(yesterday, 'day') && end.isSame(yesterday, 'day');
      const isLast7 = start.isSame(today.subtract(6, 'day'), 'day') && end.isSame(today, 'day');
      const isLast30 = start.isSame(today.subtract(29, 'day'), 'day') && end.isSame(today, 'day');
      const isLast12M = start.isSame(today.subtract(12, 'month').add(1, 'day'), 'day') && end.isSame(today, 'day');

      if (isToday) state.currentRange = 'today';
      else if (isYesterday) state.currentRange = 'yesterday';
      else if (isLast7) state.currentRange = '7d';
      else if (isLast30) state.currentRange = '30d';
      else if (isLast12M) state.currentRange = '1y';
      else state.currentRange = 'custom';
    },
    setCurrentGroup: (state, action) => {
      state.currentGroup = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAnalyticsAgentsStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getAnalyticsAgentsStats.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        state.data = action.payload.data;
        state.error = null;
      })
      .addCase(getAnalyticsAgentsStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
      });
  },
});

/* ----------------------------------------------------------
   ✅ Exports
----------------------------------------------------------- */
export const {
  resetAnalyticsAgentsStatsState,
  setCurrentRange,
  setDateRange,
  setCurrentGroup,
} = analyticsAgentsStatsSlice.actions;

export default analyticsAgentsStatsSlice.reducer;